import { forwardRef, useImperativeHandle, useRef } from 'react';
import TextFieldFormGroup from '../TextFieldFormGroup';
import { TranslationService } from '../../../services/translation.service';
import ValidationInfo from '../../../validation/validation-info';
import type IContactInformation from './models/contact-information.model';
import type IValidatable from '../validatable.interface';
import type { IServerValidationError } from '../validatable.interface';
import { ContactInformationSimpleField } from './contact-information-simple-field.enum';

export interface IContactInformationSimpleState {
  value: IContactInformation;
  validationInfo: ValidationInfo;
}

export interface IContactInformationSimpleProps {
  id: string;
  firstNameRequired?: boolean;
  lastNameRequired?: boolean;
  phoneNumberRequired?: boolean;
  emailRequired?: boolean;
  className?: string;
  state: IContactInformationSimpleState;
  onChange?: (value: IContactInformation) => void;
  onKeyUp?: (field: ContactInformationSimpleField) => void;
}

export default forwardRef<IValidatable, IContactInformationSimpleProps>(function ContactInformationSimple(
  { id, firstNameRequired = false, lastNameRequired = false, phoneNumberRequired = false, emailRequired = false, className = '', state, onChange, onKeyUp },
  ref
) {
  // === REFERENCES ===
  const firstNameRef = useRef<IValidatable>(null!);
  const lastNameRef = useRef<IValidatable>(null!);
  const phoneNumberRef = useRef<IValidatable>(null!);
  const emailRef = useRef<IValidatable>(null!);

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      return new ValidationInfo('', [], {
        firstName: firstNameRef.current.checkValidity(),
        lastName: lastNameRef.current.checkValidity(),
        phoneNumber: phoneNumberRef.current.checkValidity(),
        email: emailRef.current.checkValidity(),
      });
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo('', [], {
        firstName: firstNameRef.current.handleServerValidationErrors(errors),
        lastName: lastNameRef.current.handleServerValidationErrors(errors),
        phoneNumber: phoneNumberRef.current.handleServerValidationErrors(errors),
        email: emailRef.current.handleServerValidationErrors(errors),
      });
    },
  }));

  return (
    <div className={`contact-information-simple ${className}`}>
      <div className="d-flex flex-column flex-sm-row flex-sm-nowrap">
        <TextFieldFormGroup
          ref={firstNameRef}
          inputId={`${id}-first-name`}
          serverValidationName="firstName"
          labelText={TranslationService.get('common.firstname')}
          pattern="\+?[0-9]+"
          required={firstNameRequired}
          autocomplete="given-name"
          type="text"
          className="w-100 pe-sm-2"
          state={{ value: state.value.firstName, validationInfo: state.validationInfo.children.firstName ?? new ValidationInfo() }}
          onChange={(value) => onChange?.({ ...state.value, firstName: value })}
          onKeyUp={() => onKeyUp?.(ContactInformationSimpleField.firstName)}
        />
        <TextFieldFormGroup
          ref={lastNameRef}
          inputId={`${id}-last-name`}
          serverValidationName="lastName"
          labelText={TranslationService.get('common.lastname')}
          required={lastNameRequired}
          autocomplete="family-name"
          type="text"
          className="w-100 ps-sm-2"
          state={{ value: state.value.lastName, validationInfo: state.validationInfo.children.lastName ?? new ValidationInfo() }}
          onChange={(value) => onChange?.({ ...state.value, lastName: value })}
          onKeyUp={() => onKeyUp?.(ContactInformationSimpleField.lastName)}
        />
      </div>
      <div className="d-flex flex-column flex-sm-row flex-sm-nowrap">
        <TextFieldFormGroup
          ref={phoneNumberRef}
          inputId={`${id}-phone-number`}
          serverValidationName="phoneNumber"
          labelText={TranslationService.get('common.phonenumber')}
          pattern="\+?[0-9]+"
          required={phoneNumberRequired}
          autocomplete="tel"
          type="tel"
          className="w-100 pe-sm-2 mb-sm-0"
          state={{ value: state.value.phoneNumber, validationInfo: state.validationInfo.children.phoneNumber ?? new ValidationInfo() }}
          onChange={(value) => onChange?.({ ...state.value, phoneNumber: value })}
          onKeyUp={() => onKeyUp?.(ContactInformationSimpleField.phoneNumber)}
        />
        <TextFieldFormGroup
          ref={emailRef}
          inputId={`${id}-email`}
          serverValidationName="email"
          labelText={TranslationService.get('common.email')}
          required={emailRequired}
          autocomplete="email"
          type="email"
          className="w-100 ps-sm-2 mb-0"
          state={{ value: state.value.email, validationInfo: state.validationInfo.children.email ?? new ValidationInfo() }}
          onChange={(value) => onChange?.({ ...state.value, email: value })}
          onKeyUp={() => onKeyUp?.(ContactInformationSimpleField.email)}
        />
      </div>
    </div>
  );
});
